import { motion } from "framer-motion";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import heroImage from "@/assets/dest-marbella-new.webp";
import imgChauffeur from "@/assets/lifestyle-chauffeur.webp";
import imgDining from "@/assets/lifestyle-dining-ibiza.webp";
import imgWellness from "@/assets/lifestyle-wellness.webp";
import imgBeachclub from "@/assets/marbella-beachclub.png";
import imgRestaurantPlaza from "@/assets/marbella-restaurant-plaza.webp";
import imgNightlife from "@/assets/marbella-nightlife.webp";
import imgChef from "@/assets/lifestyle-chef.webp";
import imgSpecial from "@/assets/lifestyle-special.webp";

const ease = [0.22, 1, 0.36, 1] as const;

const services = [
  {
    title: "Beach Clubs",
    description:
      "Access to Marbella's most sought-after beach clubs, including Ocean Club, Nikki Beach, La Plage Casanis, and Nosso Beach Club. From relaxed daytime settings to more vibrant afternoons, each reservation is arranged with attention to timing, location, and atmosphere.",
    image: imgBeachclub,
  },
  {
    title: "Restaurant Reservations",
    description:
      "Priority reservations at Marbella's most refined dining venues, including Nobu Marbella, Cipriani Marbella, Breathe, and La Sala. Whether for long dinners or more social settings, each reservation is secured to match your preferences.",
    image: imgRestaurantPlaza,
  },
  {
    title: "Nightlife & Tables",
    description:
      "Access to Marbella's most exclusive nightlife, including Pangea, Olivia Valere, Momento, and Aqwa Mist. From guest list entry to VIP tables, every detail is arranged to ensure a smooth and elevated evening.",
    image: imgNightlife,
  },
  {
    title: "Private Transport & Chauffeur",
    description:
      "Private drivers and luxury vehicles arranged throughout your stay, ensuring seamless movement between your residence, beach clubs, and evening destinations.",
    image: imgChauffeur,
  },
  {
    title: "Private Dining & Chefs",
    description:
      "Tailored in-residence dining experiences with private chefs, offering a refined and private alternative to dining out. From intimate dinners to larger gatherings, every detail is handled within your residence.",
    image: imgChef,
  },
  {
    title: "Wellness & Personal Care",
    description:
      "In-villa wellness services, including massages, treatments, and personal care sessions designed to create moments of calm and balance throughout your stay.",
    image: imgWellness,
  },
  {
    title: "Bespoke Requests",
    description:
      "From last-minute arrangements to highly specific experiences, our team is available to handle any request with discretion and precision.",
    image: imgSpecial,
  },
];

const MarbellaLifestyle = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />

      {/* Hero */}
      <section className="relative h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Arzani Lifestyle Marbella" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/20 to-foreground/10" />
        </div>
        <div className="relative z-10 px-8 md:px-16 lg:px-20 pb-20 md:pb-28 max-w-2xl">
          <motion.span
            className="font-label text-primary-foreground/40 tracking-[0.3em] text-[9px] block mb-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 2, ease }}
          >
            Personal Services · Marbella
          </motion.span>
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground leading-[1] tracking-wide uppercase mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 2, ease }}
          >
            Arzani Lifestyle
          </motion.h1>
          <motion.p
            className="font-body text-primary-foreground/50 text-sm leading-[1.9] max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 2, ease }}
          >
            Marbella is defined by ease, space, and a more relaxed rhythm of Mediterranean living.
          </motion.p>
        </div>
      </section>

      {/* Opening */}
      <section className="py-32 md:py-44 lg:py-56">
        <div className="mx-auto px-8 md:px-16 max-w-3xl">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.6, ease }}
          >
            <span className="font-label text-accent block mb-10 tracking-[0.3em] text-[9px]">
              The Experience
            </span>
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[2] max-w-2xl mx-auto mb-8">
              Through Arzani Lifestyle, every part of your stay is arranged in advance — allowing you to move effortlessly between your residence, the coastline, and Marbella's most sought-after settings. From beach clubs and dining to transport and private services, each detail is aligned to create a seamless experience throughout your time on the Costa del Sol.
            </p>
            <div className="divider-subtle mx-auto mt-14" />
          </motion.div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-28 md:py-40 bg-warm-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: "radial-gradient(circle at 1px 1px, hsl(var(--primary-foreground)) 1px, transparent 0)",
              backgroundSize: "48px 48px",
            }}
          />
        </div>
        <div className="container-luxury max-w-2xl text-center relative z-10">
          <motion.span
            className="font-label text-bronze text-[10px] tracking-[0.35em] block mb-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.8, ease }}
          >
            Philosophy
          </motion.span>

          <motion.h2
            className="font-display text-2xl md:text-4xl lg:text-5xl text-primary-foreground tracking-wide uppercase leading-[1.1] mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.8, delay: 0.1, ease }}
          >
            In Marbella, the Experience
            <br />Is Shaped by Simplicity
          </motion.h2>

          <motion.div
            className="w-10 h-px bg-bronze/30 mx-auto mb-10"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.25, ease }}
          />

          <motion.p
            className="font-body text-primary-foreground/50 text-sm md:text-base leading-[2] mb-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.8, delay: 0.35, ease }}
          >
            Long days, open spaces, and a slower pace define the destination. Through Arzani, every element is handled in advance — from reservations and movement to in-residence services — allowing your time to unfold naturally, without unnecessary planning or interruption.
          </motion.p>
        </div>
      </section>

      {/* Services header */}
      <section className="pt-32 md:pt-44 pb-16 md:pb-20">
        <div className="mx-auto px-8 md:px-16 max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.6, ease }}
          >
            <span className="font-label text-accent/60 block mb-8 tracking-[0.3em] text-[9px]">
              Services
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground tracking-wide uppercase leading-[1.1]">
              What We Arrange
            </h2>
          </motion.div>
        </div>
      </section>

      {/* Services — alternating layout */}
      {services.map((service, index) => {
        const isEven = index % 2 === 0;
        return (
          <section key={service.title} className="mb-0">
            <motion.div
              className="grid grid-cols-1 lg:grid-cols-12 min-h-[70vh]"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.6, ease }}
            >
              <div className={`lg:col-span-7 relative overflow-hidden ${!isEven ? "lg:order-2" : ""}`}>
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full min-h-[50vh] lg:min-h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className={`lg:col-span-5 flex items-center ${!isEven ? "lg:order-1" : ""}`}>
                <div className={`px-8 md:px-16 lg:px-16 xl:px-20 py-20 lg:py-0 ${!isEven ? "lg:pl-20 xl:pl-24" : "lg:pr-20 xl:pr-24"}`}>
                  <span className="font-label text-accent/60 block mb-6 tracking-[0.3em] text-[8px]">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-2xl md:text-3xl text-foreground mb-6 tracking-wide uppercase leading-[1.15]">
                    {service.title}
                  </h3>
                  <div className="w-8 h-px bg-foreground/10 mb-8" />
                  <p className="font-body text-muted-foreground text-sm leading-[2] max-w-sm mb-8">
                    {service.description}
                  </p>
                  <a
                    href="https://wa.me/message/2XEKJ5STR2IZJ1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-8 py-3 border border-foreground/20 text-foreground font-label tracking-[0.2em] text-[10px] hover:bg-foreground hover:text-background transition-all duration-500"
                  >
                    Choose
                  </a>
                </div>
              </div>
            </motion.div>
          </section>
        );
      })}

      {/* Statement */}
      <section className="py-32 md:py-44 lg:py-52">
        <div className="mx-auto px-8 max-w-3xl text-center">
          <motion.p
            className="font-display text-3xl md:text-5xl lg:text-6xl text-foreground leading-[1.1] tracking-wide"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.6, ease }}
          >
            Everything is already
            <br />arranged.
          </motion.p>
        </div>
      </section>

      {/* Closing */}
      <section className="py-32 md:py-44 lg:py-52 bg-secondary/40">
        <div className="mx-auto px-8 max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.6, ease }}
          >
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[2] mb-12">
              A destination defined by ease,
              <br />experienced without interruption.
            </p>
            <p className="font-display text-4xl md:text-6xl lg:text-7xl text-foreground leading-[1] tracking-[0.15em] uppercase mb-4">
              Marbella,
            </p>
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[2]">
              the Arzani way.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-36 md:py-48 bg-warm-black">
        <div className="mx-auto px-8 md:px-16 max-w-xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.6, ease }}
          >
            <span className="font-label text-bronze/60 block mb-10 tracking-[0.3em] text-[9px]">
              Contact
            </span>
            <h2 className="font-display text-3xl md:text-4xl text-primary-foreground mb-6 leading-[1.15] tracking-wide">
              Have a specific request?
            </h2>
            <p className="font-body text-primary-foreground/40 text-sm leading-[2] mb-14">
              Our team is available to assist.
            </p>
            <a
              href="https://wa.me/message/2XEKJ5STR2IZJ1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-4 border border-primary-foreground/20 text-primary-foreground/70 font-label tracking-[0.25em] text-[10px] hover:bg-primary-foreground/5 hover:border-primary-foreground/30 transition-all duration-700"
            >
              Contact via WhatsApp
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MarbellaLifestyle;
